import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { type AppSettingKey, KNOWN_APP_SETTING_KEYS } from "@/hooks/appSettingKeys";

/** Warn once per bad key, so an unknown key in a render loop doesn't flood. */
const warned = new Set<string>();

/**
 * Reads a boolean flag from the `app_settings` table.
 *
 * **Fails closed to `false`** while loading, on a missing row, and on any query
 * error — a flag Kasandra hasn't flipped on yet must never accidentally read as
 * "on". That direction is deliberate and load-bearing; don't "fix" it.
 *
 * ⚠️ Note WHERE the error handling actually happens. supabase-js v2 RESOLVES
 * `{ data: null, error }` on a failed query rather than throwing, so the
 * `catch` below never fires for a normal DB or RLS error — it is there only for
 * a genuine exception like a network abort. The thing that actually turns a
 * failed read into `false` is the `!!res.data?.enabled` coercion, because
 * `data` is `null`. Same outcome, and the comment here used to credit the wrong
 * line, which is worth stating plainly: it's the same mechanism behind house
 * rule 16, where a `try/catch` around a supabase call reads as handled and
 * isn't.
 *
 * Reads once per mount. No caching, no dedupe, no realtime — see #410's
 * out-of-scope list before adding any of those.
 */
export function useAppSetting(key: AppSettingKey): boolean {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Untyped callers (a `as any`, a JS import, a stale generated file) can
    // still reach this at runtime. Warn, never throw, and never change the
    // returned value — a flag read must not be able to break a render.
    if (import.meta.env.DEV && !KNOWN_APP_SETTING_KEYS.includes(key) && !warned.has(key)) {
      warned.add(key);
      console.warn(
        `[useAppSetting] Unknown app_settings key "${key}". Known keys: ${KNOWN_APP_SETTING_KEYS.join(", ")}. ` +
          `This will read as false forever, which is indistinguishable from the flag being off.`,
      );
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await supabase.from("app_settings").select("enabled").eq("key", key).maybeSingle();
        if (!cancelled) setEnabled(!!res.data?.enabled);
      } catch {
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  return enabled;
}
